/* eslint-disable no-param-reassign */
import type { Data } from '../../specProcessor';
import type { StandardChartLayerSpec } from '../../../../core/layer';
import type { Spec as MarkerLineLayerSpec } from '../../../../layer/preset/line/markerLine';
import * as utils from '../../../../util';
import {
  BaseSpec,
  ILayerTypeSpec,
  createDataTypeValidator,
  createEncodingValidator,
  createLayerProcessor
} from '../baseProcessor';

export type Spec = BaseSpec;

// 扩展ILayerTypeSpec以包含__isEncodingValid属性
interface MarkerLineLayerTypeWithFields extends ILayerTypeSpec {
  __isEncodingValid?: boolean;
}

type MarkerLineLayerType = StandardChartLayerSpec &
  MarkerLineLayerSpec &
  MarkerLineLayerTypeWithFields;

// 创建大事指标折线图数据类型验证器
const validateMarkerLineDataTypes = createDataTypeValidator('markerLine', [
  { field: 'x', type: ['string', 'date'] },
  { field: 'y', type: 'number' },
  { field: 'y2', type: 'string' },
  { field: 'subTitle', type: 'string', required: false },
  { field: 'desc', type: 'string', required: false },
  { field: 'link', type: 'string', required: false }
]);

// 创建大事指标折线图编码验证器
const validateMarkerLineEncoding = createEncodingValidator([
  { field: 'x' },
  { field: 'y' },
  { field: 'y2' },
  { field: 'subTitle', required: false },
  { field: 'desc', required: false },
  { field: 'link', required: false }
]);

// 额外的检查函数 - 验证数据规则
const additionalMarkerLineChecks = (layer: MarkerLineLayerType, data: Data) => {
  const { encoding } = layer;
  const dataIndex = layer.dataIndex || 0;
  const values = data[dataIndex]?.values || [];

  // 如果数据为空，直接返回失败
  if (!values.length) {
    utils.logger.warn('warning', `No data found for "${layer.type}" layer`);
    layer.__isEncodingValid = false;
    return;
  }

  // 检查X轴数据是否至少有2个不同的值
  const uniqueXValues = new Set(values.map(item => item[encoding.x]));
  if (uniqueXValues.size < 2) {
    utils.logger.warn(
      'warning',
      `The "${layer.type}" layer requires at least 2 different x values`
    );
    layer.__isEncodingValid = false;
  }

  // 检查是否至少有一个数据点包含y2值（事件标记）
  const hasMarkers = values.some(item => item[encoding.y2] != null);
  if (!hasMarkers) {
    utils.logger.warn(
      'warning',
      `The "${layer.type}" layer requires at least one data point with a y2 value`
    );
    layer.__isEncodingValid = false;
  }
};

// 创建并导出大事指标折线图处理器
export const markerLine = createLayerProcessor<MarkerLineLayerType>(
  'markerLine',
  validateMarkerLineEncoding,
  validateMarkerLineDataTypes,
  additionalMarkerLineChecks
);
